import { Alert, AlertTitle, Button } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { STRIPE_UNAVAILABLE_MESSAGE } from 'lib/stripe/userStripe';

export type StripeAccountUnavailableProps = {
  /** The error from `useUserStripeState()`. Its message is the body copy. */
  error?: Error | null;
  /** `retry` from `useUserStripeState()`. Renders a Try again button. */
  onRetry?: () => void;
  /** Only offer a retry for a transport failure — `retryable` from the hook. */
  retryable?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * The one "payment cannot be set up" surface, shown wherever `useUserStripeState`
 * reports an error.
 *
 * No publishable key could be resolved for this user, so Stripe.js was
 * deliberately never loaded — mounting `<Elements>` on the other account's key
 * would produce a card form whose intent can never be confirmed, and mounting
 * it on nothing leaves the surface spinning forever because PaymentElement
 * never becomes ready. Every payment surface renders this instead.
 *
 * Red rather than amber: this is a broken deploy or an unreachable backend, not
 * something the user did or can correct. The copy says so — it never asks them
 * to fix their card, their account, or their details.
 */
export default function StripeAccountUnavailable({ error, onRetry, retryable, sx }: StripeAccountUnavailableProps) {
  return (
    <Alert
      role="alert"
      severity="error"
      sx={{ my: 2, ...sx }}
      action={
        retryable && onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>Payment unavailable</AlertTitle>
      {error?.message || STRIPE_UNAVAILABLE_MESSAGE}
    </Alert>
  );
}
